// @core/stores/affiliate/AffiliateTrackingStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { affiliateApi } from '@core/api';
import type {
  Click,
  ConversionResponse,
  TrackClickResponse,
  TrackConversionRequest,
} from '@core/types/affiliate';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AffiliateTrackingStore {
  // List state
  items: Click[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Click | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Click>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Click | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Click | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * List clicks
   */
  async ListClicks(params?: PaginatedRequest & { affiliate_id?: string | number | boolean; campaign_id?: string | number | boolean; offer_id?: string | number | boolean; period_end?: string | number | boolean; period_start?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await affiliateApi.ListClicks(params);

      runInAction(() => {
        this.items = response.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Track click and redirect
   */
  async TrackClick(tracking_code: string, params?: { sub1?: string | number | boolean; sub2?: string | number | boolean; sub3?: string | number | boolean; sub4?: string | number | boolean; sub5?: string | number | boolean; }): Promise<TrackClickResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.TrackClick(tracking_code, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Track conversion
   */
  async TrackConversion(data: TrackConversionRequest): Promise<ConversionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.TrackConversion(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const affiliateTrackingStore = new AffiliateTrackingStore();
